import { useMemo } from 'react'
import PropTypes from 'prop-types'

import AvatarMui from '@mui/material/Avatar'

import useFormatMessage from '@hooks/useFormatMessage'

import { getSizeStyle, stringAvatar } from './Avatar.utils'

Avatar.propTypes = {
  /** The user to display in the avatar */
  user: PropTypes.shape({
    nickname: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    profilePictureThumbUrl: PropTypes.string,
    fullName: PropTypes.string,
  }),

  /** Can override the variant, default is 'rounded' */
  variant: PropTypes.oneOf(['circular', 'rounded', 'square']),

  /** Size of Avatar, default is 'md' */
  size: PropTypes.oneOf(['badge', 'xs', 'sm', 'md', 'lg', 'xl', 'xxl']),
}

/**
 * Display a user's avatar. If a profile image is available, it will be displayed. Otherwise, the user's initials will be displayed.
 *
 * @param {object} props - The component props.
 * @param {object} props.user - The user object containing avatar data.
 * @param {string} [props.variant='rounded'] - The shape of the avatar.
 * @param {string} [props.size='md'] - The size of the avatar.
 * @returns {JSX.Element} The Avatar component.
 */
export default function Avatar({ user, variant = 'rounded', size = 'md', sx, ...rest }) {
  const formatMessage = useFormatMessage()
  const { backgroundColor, initials } = useMemo(() => stringAvatar(user), [user])
  const name = user?.fullName || user?.firstName || user?.nickname
  const altLabel = name ? formatMessage('ui.avatar.a11y.label', { name }) : formatMessage('ui.avatar.a11y.default')

  return (
    <AvatarMui
      alt={altLabel}
      src={user?.profilePictureThumbUrl}
      variant={variant}
      size={size}
      sx={[getSizeStyle(size), { color: 'primary.contrastText', backgroundColor }, sx]}
      {...rest}
    >
      {initials}
    </AvatarMui>
  )
}
